import type { AnyClock } from "@repo/lib/clock";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import {
  PatchUserDtoSchema,
  type RoleType,
  type Tenant,
  type User,
  type UserWRole,
} from "@repo/types";
import {
  cacheDefaults,
  messageServiceSubscribe,
  SubscriptionMap,
} from "./common";
import { apiService } from "@/services/apiService";
import { of, type Observable } from "rxjs";
import type { OnlyRequire } from "@repo/lib/types";
import { phoneNumberRepo } from "./phoneNumberRepo";
import { addressRepo } from "./addressRepo";
import { nullObservable } from "@repo/shared-client/commonObservables";
import { tenantRepo } from "./tenantRepo";
import { RepoMutationHooks } from "./repoMutationHooks";
import { baseRepo } from "./baseRepo";

type UserGetKey = { type: "get"; userId: string };
type UserGetByEmailKey = { type: "getByEmail"; email: string };
type UserListKey = { type: "list"; tenantId: string };
type UserKey = UserGetKey | UserGetByEmailKey | UserListKey;

export type UserIdPath = { userId: string | null };

export class UserRepository {
  private cache: RepoCache<string, UserWRole, ObjectKeyHasher, AnyClock>;
  private hooks = new RepoMutationHooks<UserIdPath, UserWRole>();
  private subscribeMap = new SubscriptionMap();
  private tenantUsers: Partial<Record<string, string[]>> = Object.create(null);

  // expose hook registry
  addHook = this.hooks.addHook;
  removeHook = this.hooks.removeHook;

  userToUserWRole(user: User): UserWRole;
  userToUserWRole(user?: null): null;
  userToUserWRole(user?: User | null): UserWRole | null;
  userToUserWRole(user?: User | null): UserWRole | null {
    if (!user) return null;
    const userWRole = this.cache.table.get(user.id);
    return {
      ...user,
      role: (user as UserWRole).role ?? userWRole?.role ?? ("" as RoleType),
    };
  }

  publishNewer(user: UserWRole) {
    this.cache.publish(user.id, user);
  }

  constructor() {
    this.cache = new RepoCache({
      requestKeyHasher: new ObjectKeyHasher({ sortKeys: false }),
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as UserKey;

        switch (key.type) {
          case "get": {
            const { userId } = key;
            const user = (await apiService.v1.getUser(userId)).data;
            publish(userId, this.userToUserWRole(user));
            break;
          }
          case "getByEmail": {
            const { email } = key;
            const user = (await apiService.v1.getUserByEmail({ email })).data;
            if (user) publish(user.id, this.userToUserWRole(user));
            break;
          }
          case "list": {
            const { tenantId } = key;
            const users = (await apiService.v1.getUsersInTenant(tenantId)).data;

            const array: string[] = (this.tenantUsers[tenantId] = []);
            for (const user of users) {
              array.push(user.id);
              publish(user.id, user);
            }
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const key = requestKey as UserKey;

        switch (key.type) {
          case "get": {
            return table.get(key.userId) ?? null;
          }
          case "getByEmail": {
            for (const user of table.values()) {
              if (user.email === key.email) return user;
            }
            return null;
          }
          case "list": {
            const users =
              this.tenantUsers[key.tenantId]
                ?.map((id) => table.get(id))
                .filter((user) => !!user) ?? [];
            return users;
          }
        }
      },
      affectedKeys: ({ entryData, affectsKey }) => {
        // extract
        const { id, email } = entryData;
        // affect
        affectsKey(this.getKey(id));
        affectsKey(this.getByEmailKey(email));
        for (const tenantId of Object.keys(this.tenantUsers)) {
          if (this.tenantUsers[tenantId]!.includes(id)) {
            affectsKey(this.listKey(tenantId));
          }
        }
      },
      initialSubscribe: ({ strKey, requestKey }) => {
        const key = requestKey as UserKey;
        if (key.type !== "get" && key.type !== "list") return;

        const subscription = messageServiceSubscribe(
          "changing.tenants",
          key.type === "get" ? [key.userId] : [],
          () => this.cache.fetch(key)
        );

        this.subscribeMap.addSubscription(strKey, subscription);
      },
      finalUnsubscribe: ({ strKey }) => {
        this.subscribeMap.unsubscribe(strKey);
      },
    });

    baseRepo.addRepo(this);
  }

  private getKey(userId: string): UserGetKey {
    return { type: "get", userId };
  }
  private getByEmailKey(email: string): UserGetByEmailKey {
    return { type: "getByEmail", email };
  }
  private listKey(tenantId: string): UserListKey {
    return { type: "list", tenantId };
  }

  get(
    userId: string,
    options?: GetObservableOptions
  ): Observable<UserWRole | null> {
    if (!userId) {
      return nullObservable;
    }
    const key = this.getKey(userId);
    return this.cache.observable(key, options);
  }
  getCached(userId: string): UserWRole | null {
    const key = this.getKey(userId);
    return this.cache.collectIfUnexpired(key, null);
  }

  getByEmail(
    email: string,
    options?: GetObservableOptions
  ): Observable<UserWRole | null> {
    if (!email) {
      return nullObservable;
    }
    const key = this.getByEmailKey(email);
    return this.cache.observable(key, options);
  }
  getByEmailCached(email: string): UserWRole | null {
    const key = this.getByEmailKey(email);
    return this.cache.collectIfUnexpired(key, null);
  }

  list(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<UserWRole[]> {
    if (!tenantId) {
      return of([]);
    }
    const key = this.listKey(tenantId);
    return this.cache.observable(key, options);
  }
  listCached(tenantId: string): UserWRole[] | null {
    const key = this.listKey(tenantId);
    return this.cache.collectIfUnexpired(key, null);
  }

  async create(data: Pick<UserWRole, "email">): Promise<UserWRole> {
    const hooks = this.hooks.execute({ userId: null }, "create");
    data = (await hooks.before(data)) as Pick<UserWRole, "email">;

    let created = this.userToUserWRole(
      (await apiService.v1.createNewUser({ userData: { email: data.email } }))
        .data
    );

    created = await hooks.after(created);

    // publish new user
    this.cache.publish(created.id, created);

    return created;
  }

  async createSelf(
    data: Pick<UserWRole, "firstName" | "lastName">
  ): Promise<UserWRole> {
    const hooks = this.hooks.execute({ userId: null }, "create");
    data = (await hooks.before(data)) as Pick<
      UserWRole,
      "firstName" | "lastName"
    >;

    let created = this.userToUserWRole(
      (
        await apiService.v1.createMyUser({
          userData: { firstName: data.firstName, lastName: data.lastName },
        })
      ).data
    );

    created = await hooks.after(created);

    // publish new user
    this.cache.publish(created.id, created);

    return created;
  }

  async update(userId: string, data: Partial<UserWRole>): Promise<UserWRole> {
    if (!userId) {
      throw new Error("Failed to update user: userId is blank.");
    }

    const hooks = this.hooks.execute({ userId }, "update");
    data = await hooks.before(data);

    let updated = this.userToUserWRole(
      (await apiService.v1.updateUser(userId, PatchUserDtoSchema.parse(data)))
        .data
    );

    updated = await hooks.after(updated);

    // publish updated user
    this.cache.publish(updated.id, updated);

    return updated;
  }

  refetchForTenant(tenant: Tenant) {
    const key = this.listKey(tenant.id);
    const currentUserIds = this.tenantUsers[tenant.id];
    if (!currentUserIds || !this.cache.isUsed(key)) return;

    // check if passed user ids are different than cached user ids
    let diff = false;
    const userIds = tenantRepo.extractUserIds(tenant);
    const currentSet = new Set(currentUserIds);
    for (const id of userIds) {
      if (!currentSet.has(id)) {
        diff = true;
        break;
      }
      currentSet.delete(id);
    }
    if (currentSet.size > 0) diff = true;

    // if different, re-run a fetch
    if (diff) this.cache.fetch(key);
  }

  invalidate(user: OnlyRequire<UserWRole, "id">) {
    this.cache.invalidateEntry(user.id);
  }

  invalidateAll() {
    this.cache.clear();
  }

  init(data?: Partial<UserWRole>): UserWRole {
    return baseRepo.init({
      firstName: "",
      lastName: "",
      name: "",
      email: "",
      emailVerified: null,
      image: "",
      salutation: "",
      lastLoginTime: new Date(),
      primaryPhone: phoneNumberRepo.init(),
      primaryAddress: addressRepo.init(),
      role: "" as RoleType,
      ...data,
    });
  }
}

export const userRepo = new UserRepository();
